import { MessageStatusEnum, MessageTypeEnum } from "common/enums";
import { Message } from "common/types";
import { useCallback } from "react";
import { sendMessageWithMedia, sendTextMessage } from "requests/messages";
import { useChatStore } from "store/useChatStore";
import { useSelectedDeviceStore } from "store/useSelectedDeviceStore";
import { mapBlobTypeToMessageTypeEnum } from "utils";
import showSnackbar from "utils/showSnackbar";

type Props = {
  currentMessages: Message[];
};

export default function useSendMessageContainerLogic(props: Props) {
  const { currentMessages } = props;
  const { selectedDevice } = useSelectedDeviceStore();
  const { selectedConversation, setOpenedChatMessages } = useChatStore();

  const updateMessagesInChatStore = useCallback((messages: Message[]) => {
    setOpenedChatMessages(messages);
  }, [setOpenedChatMessages]);

  const requestSendTextMessage = useCallback(
    (message: string, onSuccess?: VoidFunction, onFinally?: VoidFunction) => {
      sendTextMessage({
        deviceId: selectedDevice,
        jid: selectedConversation?.messages[0]?.sender,
        media: '',
        mediaType: MessageTypeEnum.text,
        fileName: '',
        message,
      })
        .then(() => {
          const newMessage: Message = {
            fromMe: true,
            id: Math.random().toString(),
            mediaUrl: undefined,
            mediaBinary: undefined,
            messageId: Math.random().toString() + message,
            messageType: MessageTypeEnum.text,
            receivedAt: undefined,
            sender: selectedConversation?.messages[0]?.sender,
            senderName: selectedConversation?.messages[0]?.senderName,
            shouldSuggestFinish: false,
            status: MessageStatusEnum.PENDING,
            textContent: message,
          };
          
          updateMessagesInChatStore([newMessage, ...(currentMessages || [])]);
          onSuccess && onSuccess();
        })
        .catch((error) => console.error(error))
        .finally(() => onFinally && onFinally());
    },
    [
      selectedDevice,
      currentMessages,
      selectedConversation?.messages,
      updateMessagesInChatStore,
    ]
  );

  const requestSendMediaMessage = useCallback(
    async (
      mediaBlob: Blob,
      fileName?: string,
      onSuccess?: VoidFunction,
      onFailed?: VoidFunction,
      onFinally?: VoidFunction
    ) => {
      try {
        const mediaType = mapBlobTypeToMessageTypeEnum(mediaBlob);
        await sendMessageWithMedia(
          {
            deviceId: selectedDevice,
            jid: selectedConversation?.messages[0]?.sender,
            mediaType: mediaType,
            fileName: fileName,
          },
          mediaBlob
        );

        const newMessage: Message = {
          id: Math.random().toString(),
          messageType: mediaType,
          fromMe: true,
          status: MessageStatusEnum.PENDING,
          mediaUrl: undefined,
          mediaBinary: undefined,
          messageId: Math.random().toString(),
          receivedAt: undefined,
          sender: selectedConversation?.messages[0]?.sender,
          senderName: selectedConversation?.messages[0]?.senderName,
          shouldSuggestFinish: false,
          textContent: undefined,
        };

        updateMessagesInChatStore([newMessage, ...(currentMessages || [])])
        onSuccess && onSuccess();
      } catch (error) {
        showSnackbar(
          `Falha ao enviar mensagem. Tente novamente! ERR: ${error?.status}`,
          "error"
        );
        onFailed && onFailed();
        console.error(error);
      } finally {
        onFinally && onFinally();
      }
    },
    [
      selectedDevice,
      currentMessages,
      selectedConversation?.messages,
      updateMessagesInChatStore,
    ]
  );

  return {
    selectedDevice,
    selectedConversation,
    requestSendMediaMessage,
    requestSendTextMessage,
  };
}
